<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">
          {{ editMode ? "割り勘を編集" : "割り勘を追加" }}
        </h2>
        <button @click="closeModal" class="modal-close-btn" aria-label="閉じる">&times;</button>
      </div>

      <form @submit.prevent="handleSubmit" @keydown.ctrl.enter.prevent="handleSubmit" class="modal-form">
        <!-- エラーメッセージ -->
        <div v-if="errorMessage" class="error-banner">
          {{ errorMessage }}
        </div>

        <!-- 件名 -->
        <div class="form-group">
          <label class="form-label" for="warikan-subject">
            件名 <span class="required">*</span>
          </label>
          <input
            v-model="formData.subject"
            type="text"
            id="warikan-subject"
            class="form-input"
            :class="{ 'input-error': errors.subject }"
            placeholder="例: コンビニで買った飲み物"
            required
          />
          <span v-if="errors.subject" class="error-text">{{
            errors.subject
          }}</span>
        </div>

        <!-- 金額と通貨 -->
        <div class="form-row">
          <div class="form-group">
            <label class="form-label" for="warikan-amount">
              金額 <span class="required">*</span>
            </label>
            <input
              v-model="displayCost"
              type="text"
              id="warikan-amount"
              class="form-input"
              :class="{ 'input-error': errors.amount }"
              placeholder="1000"
              @focus="handleCostFocus"
              @input="handleCostInput"
              @blur="handleCostBlur"
              required
            />
            <span v-if="errors.amount" class="error-text">{{
              errors.amount
            }}</span>
          </div>
          <div class="form-group">
            <label class="form-label" for="warikan-currency">通貨</label>
            <select
              v-model="formData.currency"
              id="warikan-currency"
              class="form-input"
              @change="handleCurrencyChange"
            >
              <option
                v-for="currency in currencyOptions"
                :key="currency"
                :value="currency"
              >
                {{ formatCurrencyOption(currency) }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="requiresRate" class="rate-panel">
          <div class="rate-summary">
            <span>1 {{ formData.currency }} =</span>
            <strong v-if="currentRate">{{ currentRate }} JPY</strong>
            <strong v-else>未設定</strong>
          </div>
          <div class="rate-controls">
            <input
              v-model="manualRate"
              type="number"
              min="0.00000001"
              step="0.00000001"
              class="form-input"
              :class="{ 'input-error': errors.rate }"
              placeholder="例: 155.12345678"
            />
            <button
              type="button"
              class="btn btn-secondary rate-btn"
              :disabled="isRateSaving"
              @click="saveManualRate"
            >
              手動設定
            </button>
            <button
              type="button"
              class="btn btn-secondary rate-btn"
              :disabled="isRateSaving"
              @click="refreshRate"
            >
              最新レート取得
            </button>
          </div>
          <span v-if="errors.rate" class="error-text">{{ errors.rate }}</span>
        </div>

        <!-- 支払者 -->
        <div class="form-group">
          <label class="form-label" for="warikan-paid-by">
            支払者 <span class="required">*</span>
          </label>
          <select
            v-model="formData.paidBy"
            id="warikan-paid-by"
            class="form-input"
            :class="{ 'input-error': errors.paidBy }"
            required
          >
            <option value="">選択してください</option>
            <option
              v-for="member in shioriMembers"
              :key="member.MEMBER_CODE"
              :value="member.MEMBER_CODE"
            >
              {{ member.MEMBER_NAME }}
            </option>
          </select>
          <span v-if="errors.paidBy" class="error-text">{{
            errors.paidBy
          }}</span>
        </div>

        <!-- 割り勘参加者 -->
        <div class="form-group">
          <label class="form-label">
            割り勘参加者 <span class="required">*</span>
          </label>
          <div class="checkbox-group">
            <label
              v-for="member in shioriMembers"
              :key="member.MEMBER_CODE"
              class="checkbox-label"
            >
              <input
                type="checkbox"
                :value="member.MEMBER_CODE"
                v-model="formData.selectedMembers"
                class="checkbox-input"
              />
              <span>{{ member.MEMBER_NAME }}</span>
            </label>
          </div>
          <span v-if="errors.selectedMembers" class="error-text">{{
            errors.selectedMembers
          }}</span>
        </div>

        <!-- メモ -->
        <div class="form-group">
          <label class="form-label" for="warikan-memo">メモ</label>
          <textarea
            v-model="formData.memoText"
            id="warikan-memo"
            class="form-input form-textarea"
            placeholder="例: レシートは太郎が保管、現地精算予定など"
            rows="3"
            maxlength="1000"
          ></textarea>
        </div>

        <!-- フォームアクション -->
        <div class="form-actions">
          <button type="button" @click="closeModal" class="btn btn-secondary">
            キャンセル
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "処理中..." : editMode ? "更新" : "作成" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from "vue";
import { useWarikanStore } from "@/store/warikan-store";
import { useShioriMemberStore } from "@/store/shiori-member-store";
import {
  buildCurrencyOptions,
  formatCurrencyOption,
  getRateToJpy,
  normalizeCurrency,
} from "@/utils/exchange-rate";

export default {
  name: "CreateWarikanModal",
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    shioriId: {
      type: String,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    warikanData: {
      type: Object,
      default: null,
    },
    members: {
      type: Array,
      default: null,
    },
    shareToken: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "create", "update", "access-denied"],
  setup(props, { emit }) {
    const warikanStore = useWarikanStore();
    const shioriMemberStore = useShioriMemberStore();

    const formData = ref({
      subject: "",
      amount: 0,
      currency: "JPY",
      paidBy: "",
      selectedMembers: [],
      memoText: "",
    });

    const displayCost = ref("0");
    const errors = ref({});
    const errorMessage = ref("");
    const isSubmitting = ref(false);
    const isRateSaving = ref(false);
    const manualRate = ref("");
    const currencyOptions = computed(() =>
      buildCurrencyOptions(formData.value.currency),
    );

    const shioriMembers = computed(() => {
      if (props.members) return props.members;
      return shioriMemberStore.members[props.shioriId] || [];
    });

    const selectedCurrency = computed(() =>
      normalizeCurrency(formData.value.currency),
    );

    const requiresRate = computed(() => selectedCurrency.value !== "JPY");

    const currentRate = computed(() =>
      getRateToJpy(selectedCurrency.value, warikanStore.exchangeRateMap),
    );

    // フォームリセット
    const resetForm = () => {
      formData.value = {
        subject: "",
        amount: 0,
        currency: "JPY",
        paidBy: "",
        selectedMembers: [],
        memoText: "",
      };
      displayCost.value = "0";
      errors.value = {};
      errorMessage.value = "";
      manualRate.value = "";
    };

    // モーダルを閉じる
    const closeModal = () => {
      emit("update:modelValue", false);
      resetForm();
    };

    // バリデーション
    const validateForm = () => {
      errors.value = {};
      let isValid = true;

      if (!formData.value.subject.trim()) {
        errors.value.subject = "件名を入力してください";
        isValid = false;
      }

      if (formData.value.amount <= 0) {
        errors.value.amount = "金額を入力してください";
        isValid = false;
      }

      if (selectedCurrency.value === "JPY" && !Number.isInteger(formData.value.amount)) {
        errors.value.amount = "JPYは整数で入力してください";
        isValid = false;
      }

      if (requiresRate.value && !currentRate.value) {
        errors.value.rate = "保存前に為替レートを設定してください";
        isValid = false;
      }

      if (!formData.value.paidBy) {
        errors.value.paidBy = "支払者を選択してください";
        isValid = false;
      }

      if (formData.value.selectedMembers.length === 0) {
        errors.value.selectedMembers = "参加者を選択してください";
        isValid = false;
      }

      return isValid;
    };

    // 金額入力処理
    const handleCostInput = (e) => {
      const pattern = requiresRate.value ? /[^0-9.]/g : /[^0-9]/g;
      let value = e.target.value.replace(pattern, "");
      if (requiresRate.value) {
        const parts = value.split(".");
        value = `${parts[0].replace(/^0+(\d)/, "$1")}${
          parts.length > 1 ? `.${parts.slice(1).join("").slice(0, 2)}` : ""
        }`;
      } else {
        value = value.replace(/^0+(\d)/, "$1");
      }
      displayCost.value = value || "0";
      formData.value.amount = requiresRate.value
        ? Number(parseFloat(value || "0").toFixed(2))
        : parseInt(value) || 0;
    };

    const handleCostFocus = () => {
      if (displayCost.value === '0') {
        displayCost.value = '';
      }
    };

    const handleCostBlur = () => {
      if (formData.value.amount > 0) {
        displayCost.value = requiresRate.value
          ? Number(formData.value.amount).toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : formData.value.amount.toLocaleString();
      } else if (!displayCost.value) {
        displayCost.value = '0';
      }
    };

    const handleCurrencyChange = () => {
      formData.value.currency = selectedCurrency.value;
      if (formData.value.amount > 0) {
        formData.value.amount =
          selectedCurrency.value === "JPY"
            ? Math.round(formData.value.amount)
            : Number(formData.value.amount.toFixed(2));
        handleCostBlur();
      }
      errors.value.rate = "";
    };

    const saveManualRate = async () => {
      const rate = Number(manualRate.value);
      if (!Number.isFinite(rate) || rate <= 0) {
        errors.value.rate = "正の為替レートを入力してください";
        return false;
      }
      isRateSaving.value = true;
      errors.value.rate = "";
      try {
        if (props.shareToken) {
          await warikanStore.upsertSharedExchangeRate(
            props.shareToken,
            selectedCurrency.value,
            rate,
          );
        } else {
          await warikanStore.upsertExchangeRate(
            props.shioriId,
            selectedCurrency.value,
            rate,
          );
        }
        manualRate.value = "";
        return true;
      } catch (error) {
        errors.value.rate =
          error.response?.data?.detail || "為替レートの保存に失敗しました";
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }
        return false;
      } finally {
        isRateSaving.value = false;
      }
    };

    const refreshRate = async () => {
      isRateSaving.value = true;
      errors.value.rate = "";
      try {
        if (props.shareToken) {
          await warikanStore.refreshSharedExchangeRates(props.shareToken, [
            selectedCurrency.value,
          ]);
        } else {
          await warikanStore.refreshExchangeRates(props.shioriId, [
            selectedCurrency.value,
          ]);
        }
      } catch (error) {
        errors.value.rate =
          error.response?.data?.detail || "最新レートの取得に失敗しました";
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }
      } finally {
        isRateSaving.value = false;
      }
    };

    // 送信処理
    const handleSubmit = async () => {
      if (!validateForm()) {
        errorMessage.value = "入力内容に誤りがあります。修正してください。";
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        const members = formData.value.selectedMembers.map((memberCode) => ({
          member_code: memberCode,
        }));

        const warikanData = {
          shioriId: props.shioriId,
          subject: formData.value.subject,
          amount: formData.value.amount,
          currency: formData.value.currency,
          paidBy: formData.value.paidBy,
          members: members,
          scheduleLineNon: null, // スケジュールに紐づかない
          memoText: formData.value.memoText.trim() || null,
        };

        if (props.editMode && props.warikanData) {
          // 編集モード
          const result = props.shareToken
            ? await warikanStore.updateSharedWarikan(
                props.shareToken,
                props.warikanData.LINE_NON,
                warikanData,
              )
            : await warikanStore.updateWarikan(
                props.shioriId,
                props.warikanData.LINE_NON,
                warikanData,
              );
          emit("update", result);
        } else {
          // 新規作成モード
          const result = props.shareToken
            ? await warikanStore.createSharedWarikan(
                props.shareToken,
                warikanData,
              )
            : await warikanStore.createWarikan(warikanData);
          emit("create", result);
        }

        resetForm();
        closeModal();
      } catch (error) {
        console.error("割り勘処理エラー:", error);
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }

        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMessage.value = error.response.data.detail
              .map((err) => err.msg)
              .join(", ");
          } else {
            errorMessage.value = error.response.data.detail;
          }
        } else {
          errorMessage.value = props.editMode
            ? "割り勘の更新に失敗しました。もう一度お試しください。"
            : "割り勘の作成に失敗しました。もう一度お試しください。";
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    // モーダル表示時の処理
    watch(
      () => props.modelValue,
      async (newVal) => {
        if (newVal) {
          let exchangeRateLoadError = "";
          // メンバー情報を取得
          if (!props.members && shioriMembers.value.length === 0) {
            await shioriMemberStore.fetchMembers(props.shioriId);
          }
          if (props.shareToken) {
            try {
              await warikanStore.fetchSharedExchangeRates(props.shareToken);
            } catch (error) {
              if ([403, 404].includes(error.response?.status)) {
                emit("access-denied", error);
                return;
              }
              exchangeRateLoadError =
                error.response?.data?.detail ||
                "為替レートの取得に失敗しました。もう一度お試しください。";
            }
          } else {
            await warikanStore.fetchExchangeRates(props.shioriId);
          }

          if (props.editMode && props.warikanData) {
            // 編集モード - データを反映
            formData.value = {
              subject: props.warikanData.SUBJECT,
              amount: Number(props.warikanData.AMOUNT),
              currency: props.warikanData.CURRENCY || "JPY",
              paidBy: props.warikanData.PAYER_USER_CODE,
              selectedMembers: props.warikanData.MEMBERS || [],
              memoText: props.warikanData.MEMO_TEXT || "",
            };
            displayCost.value = Number(
              props.warikanData.AMOUNT,
            ).toLocaleString("ja-JP", {
              minimumFractionDigits:
                normalizeCurrency(props.warikanData.CURRENCY) === "JPY" ? 0 : 2,
              maximumFractionDigits:
                normalizeCurrency(props.warikanData.CURRENCY) === "JPY" ? 0 : 2,
            });
          } else {
            // 新規作成モード - 全員選択をデフォルトに
            resetForm();
            formData.value.selectedMembers = shioriMembers.value.map(
              (m) => m.MEMBER_CODE,
            );
          }

          if (exchangeRateLoadError) {
            errors.value.rate = exchangeRateLoadError;
            errorMessage.value = exchangeRateLoadError;
          }
        }
      },
    );

    watch(
      () => selectedCurrency.value,
      async (newCurrency) => {
        if (newCurrency !== "JPY" && !currentRate.value) {
          await refreshRate();
        }
      },
    );

    return {
      formData,
      displayCost,
      errors,
      errorMessage,
      isSubmitting,
      isRateSaving,
      manualRate,
      currencyOptions,
      formatCurrencyOption,
      shioriMembers,
      requiresRate,
      currentRate,
      closeModal,
      handleSubmit,
      handleCostInput,
      handleCostFocus,
      handleCostBlur,
      handleCurrencyChange,
      saveManualRate,
      refreshRate,
    };
  },
};
</script>

<style scoped>
/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 1rem;
  padding-top: 5rem;
  overflow-y: auto;
}

/* モーダルコンテナ */
.modal-container {
  background: white;
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 32rem;
  box-shadow: var(--shadow-xl);
  animation: modal-slide-up 0.3s ease-out;
  overflow: hidden;
  max-height: calc(100dvh - 1rem);
  display: flex;
  flex-direction: column;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* モーダルヘッダー */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.modal-close-btn {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
}

.modal-close-btn:hover {
  background-color: var(--color-cool-bg);
  color: var(--color-text);
}

/* フォーム */
.modal-form {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  background-color: white;
}

.form-textarea {
  min-height: 5.5rem;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input[disabled] {
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  padding-left: 0;
}

.input-error {
  border-color: #ef4444 !important;
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.25rem;
  display: block;
}

.error-banner {
  background-color: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.rate-panel {
  margin: -0.25rem 0 1.25rem;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
}

.rate-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.rate-summary strong {
  color: var(--color-text);
}

.rate-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.5rem;
}

.rate-btn {
  flex: 0 0 auto;
  padding: 0.625rem 0.8rem;
  font-size: 0.875rem;
  white-space: nowrap;
}

/* チェックボックスグループ */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: var(--color-background);
  border-radius: var(--radius-md);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
}

.checkbox-input {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
}

/* フォームアクション */
.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border-light);
  margin-top: 1.5rem;
}

.btn {
  flex: 1;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-primary {
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover {
  background-color: var(--color-background);
}

@media (max-width: 576px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  .rate-controls {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  .rate-btn {
    width: 100%;
  }
}
</style>
